import { Button } from '@chakra-ui/react'
import { useInvestorSearchContext } from '@src/components/sco/InvestorSearch/Layout/InvestorSearchContext'
import { PaywalledAction } from '@src/components/sco/PaywalledAction'
import { ScoInvestorProfileContact } from '@src/graphql-types'
import { createApolloClient } from '@src/lib/apolloClient'
import { gql } from '@src/lib/gql'
import { stringify } from 'csv-stringify/browser/esm/sync'
import { omit } from 'lodash'
import React from 'react'
import { LuDownload } from 'react-icons/lu'

type BulkDownloadInvestorsButtonProps = {
  investorProfileIds?: string[]
  isEnabled?: boolean
}

export const BulkDownloadInvestorsButton = (props: BulkDownloadInvestorsButtonProps) => {
  const investorSearchContext = useInvestorSearchContext()
  const [isLoading, setIsLoading] = React.useState(false)

  const generateCsv = async () => {
    let investorProfiles = []
    let queryResponse = null
    const companyName = prompt('Provide a label for this list:')
    if (!companyName) {
      return
    }

    const csvFileName = `investor-db-export-${companyName}-${new Date().getTime() / 1000}.csv`
    setIsLoading(true)

    try {
      queryResponse = await createApolloClient().query({
        query: gql`
          query (
            $investorProfileIds: [String]
            $hqDistanceInMiles: Int
            $hqLatitude: Float
            $hqLocation: String
            $hqLocationRegion: String
            $hqLocationCountry: String
            $hqLongitude: Float
            $investmentStages: [String]
            $investorTypes: [String]
            $isSavedByUser: Boolean
            $isVerified: Boolean
            $keywords: String
            $matchAll: Boolean
            $maximumInvestmentInDollars: FriendlyNumeral
            $minimumInvestmentInDollars: FriendlyNumeral
            $order: [[String]]
            $page: Int
            $pageSize: Int
            $preferredIndustries: [String]
            $preferredIndustriesAndVerticals: [String]
            $preferredVerticals: [String]
          ) {
            scoInvestorProfiles(
              hqDistanceInMiles: $hqDistanceInMiles
              hqLatitude: $hqLatitude
              hqLocation: $hqLocation
              hqLocationCountry: $hqLocationCountry
              hqLocationRegion: $hqLocationRegion
              hqLongitude: $hqLongitude
              investorProfileIds: $investorProfileIds
              investmentStages: $investmentStages
              investorTypes: $investorTypes
              isSavedByUser: $isSavedByUser
              isVerified: $isVerified
              keywords: $keywords
              matchAll: $matchAll
              maximumInvestmentInDollars: $maximumInvestmentInDollars
              minimumInvestmentInDollars: $minimumInvestmentInDollars
              order: $order
              page: $page
              pageSize: $pageSize
              preferredIndustries: $preferredIndustries
              preferredIndustriesAndVerticals: $preferredIndustriesAndVerticals
              preferredVerticals: $preferredVerticals
            ) {
              id
              items {
                id
                investorProfileId
                title
                shortTitle
                description
                fullDescription
                hqLocation
                logoUrl
                websiteUrl
                facebookUrl
                linkedInUrl
                twitterUrl
                applicationUrl
                crunchbaseRank
                investorTypes
                primaryInvestorType
                preferredIndustriesAndVerticals
                minimumInvestmentInDollars
                maximumInvestmentInDollars
                minimumDealSizeInDollars
                maximumDealSizeInDollars
                maxFundSizeInDollars
                totalInvestmentsCount
                totalInvestmentsTTMCount
                dryPowderInDollars
                isVerified
                investmentStages
                investorProfileContacts {
                  id
                  investorProfileContactId
                  investorProfileId
                  indexPosition
                  firstName
                  lastName
                  title
                  email
                  emailReputationScore
                  phone
                  pictureUrl
                  linkedInUrl
                  currentUserHasSaved
                  currentUserHasRequested
                }
                currentUserHasSaved
              }
              pagination {
                itemCount
                hasNextPage
              }
            }
          }
        `,
        variables: {
          ...(props.investorProfileIds?.length > 0
            ? {
                investorProfileIds: props.investorProfileIds,
              }
            : investorSearchContext.filterValues),
          isSavedByUser: props?.investorProfileIds?.length > 0 ? null : true,
          pageSize: 10000,
          page: 1,
        },
        fetchPolicy: 'network-only',
      })
      investorProfiles = queryResponse.data.scoInvestorProfiles.items
    } catch (error) {
      console.log(error)
      alert('There was an error generating the CSV. Please try again.')
    }

    const mappedProfiles = investorProfiles.map(profile => {
      let mappedProfile = {
        ...omit(profile, [
          '__typename',
          'investorProfileId',
          'id',
          'investorProfileContacts',
          'createdAt',
          'updatedAt',
        ]),
      }
      profile.investorProfileContacts.forEach((contact: ScoInvestorProfileContact, idx: number) => {
        const mappedContact = omit(contact, [
          '__typename',
          'id',
          'investorProfileId',
          'investorProfileContactId',
          'createdAt',
          'updatedAt',
        ])
        const keys = Object.keys(mappedContact)
        const namedContact = {}
        keys.forEach(key => {
          namedContact[`contact_${idx + 1}_${key}`] = mappedContact[key]
        })
        mappedProfile = { ...mappedProfile, ...namedContact }
      })
      return mappedProfile
    })

    const allKeys = mappedProfiles.reduce((keys, obj) => {
      Object.keys(obj).forEach(key => keys.add(key))
      return keys
    }, new Set())

    const columns = Array.from(allKeys as Set<string>)

    const csvData = stringify(mappedProfiles, {
      header: true,
      columns: columns as readonly string[],
    })

    setIsLoading(false)

    downloadCsv(csvData, csvFileName)
  }

  return (
    <PaywalledAction
      contentPermission={'canAccessFGInvestorContacts'}
      upgradeCtaButtonProps={{ variant: 'outline', size: 'sm' }}
    >
      <Button loading={isLoading} size={'sm'} variant={'outline'} onClick={generateCsv}>
        <LuDownload />
        {'Download '}
        {props.investorProfileIds?.length ? `${props.investorProfileIds?.length} Selected` : 'All'}
      </Button>
    </PaywalledAction>
  )
}

export const downloadCsv = (csvDataString: string, fileName: string) => {
  const csvDataBlob = new Blob([csvDataString], { type: 'text/csv;charset=utf-8;' })
  const csvUrl = window.URL.createObjectURL(csvDataBlob)
  const link = document.createElement('a')
  link.href = csvUrl
  link.setAttribute('download', fileName)
  link.click()
  link.remove()
}
